/**
 * 小游戏能不能开局（I 03:2x 批的入口方案：只在家、醒着时能玩）。只读现有的家园快照（useActiveHome），不另造读口；
 * 「在不在家、在做什么」照小窝场景同一个口径（HomeScene 的 homeLifeOf）：有 life 只看 life.activity.kind，没有 life 才看 presence。
 * 读不到就当不能玩，不猜：在家但没有 life（不知道醒着没有）也算读不到。
 */
import type { HomeLife, HomeSnapshot, PetPresence } from "@/shared/contracts";
import { formatDuration } from "@/shared/time/clock";

export type PlayStatus = "ok" | "away" | "asleep" | "arriving" | "unknown";

const AWAY: ReadonlySet<PetPresence> = new Set(["in_transit", "at_destination", "visiting", "returning"]);

/** 当前这只宠物的 life（快照里的 pets，旧快照在 household.pets）；没有就是 null */
export function currentLife(snapshot: HomeSnapshot | undefined): HomeLife | null {
  if (!snapshot) return null;
  const petId = snapshot.pet.pet_id;
  return (snapshot.pets ?? snapshot.household?.pets)?.find((pet) => pet.pet_id === petId)?.life ?? null;
}

export function playStatus(snapshot: HomeSnapshot | undefined): PlayStatus {
  if (!snapshot) return "unknown";
  const life = currentLife(snapshot);
  if (life) {
    switch (life.activity.kind) {
      case "awake":
        return "ok";
      case "sleeping":
        return "asleep";
      case "away":
        return "away";
      case "arriving":
        return "arriving";
      default:
        return "unknown";
    }
  }
  return AWAY.has(snapshot.presence) ? "away" : "unknown";
}

/** 不能开局时给主人的一句（带 TA 的名字） */
export function playNotice(status: Exclude<PlayStatus, "ok">, name: string): string {
  switch (status) {
    case "away":
      return `${name}出门了，回家再一起玩。`;
    case "asleep":
      return `${name}睡着了，醒了再玩。`;
    case "arriving":
      return `${name}刚到家，先歇一会儿再玩。`;
    case "unknown":
      return `现在还看不清${name}在做什么，过一会儿再来。`;
  }
}

/**
 * 什么时候能玩（R27 第 4 条：不能开局时别只留一张白纸）。只用快照里给了的时刻，不按本机时钟推作息、不猜：
 * - 睡着：作息说下一次是「起床」（rhythm.next_change＝"wake"）且时刻在后面，才写还要多久醒；否则只说醒了就能玩；
 * - 刚到家：activity.until 是这段歇息的结束，还有一分钟以上才写时长；
 * - 出门：家园快照里没有回家时刻（JourneyBrief 不带），只说回到家、醒着就能玩；
 * - 读不到：不再多说（上面那句已经让过一会儿再来）。
 * `now` 是服务器校准过的现在（serverNow）。
 */
export function playWhen(status: Exclude<PlayStatus, "ok">, life: HomeLife | null, now: number): string | null {
  switch (status) {
    case "asleep": {
      const rhythm = life?.rhythm ?? null;
      const at = rhythm?.next_change === "wake" && rhythm.next_change_at ? Date.parse(rhythm.next_change_at) : NaN;
      return !Number.isNaN(at) && at > now ? `大约再过 ${formatDuration(at - now)}醒来，醒了就能一起玩。` : "醒了就能一起玩。";
    }
    case "arriving": {
      const at = life?.activity.until ? Date.parse(life.activity.until) : NaN;
      return !Number.isNaN(at) && at - now >= 60_000 ? `歇好了就能一起玩，大约再过 ${formatDuration(at - now)}。` : "歇好了就能一起玩。";
    }
    case "away":
      return "回到家、醒着的时候就能一起玩。";
    case "unknown":
      return null;
  }
}

export type GameId = "snake" | "basket" | "flight";

/** 三款游戏：标题与游戏自己开始页的标题一致，一句玩法从开始页的规则改写，最窄的屏一行放得下 */
export const GAMES: ReadonlyArray<{ id: GameId; path: string; title: string; line: string; icon: "sprout" | "sparkle" | "plane" }> = [
  { id: "snake", path: "/play/snake", title: "贪吃猫狗", line: "吃零食变大，钻窄口、撞纸箱。", icon: "sprout" },
  { id: "basket", path: "/play/basket", title: "宠物篮球", line: "按住蓄力，松手投篮。", icon: "sparkle" },
  { id: "flight", path: "/play/flight", title: "宠物战机", line: "躲开来袭，撑满时间就通关。", icon: "plane" },
];
